import { useState } from 'react';
import {
  useAdminProvisionOrganizationMutation,
  OrganizationTier,
  TrialDurationWeeks,
} from 'src/generated/graphql';
import { Link } from 'react-router-dom';
import { routes } from 'src/routes';

type ProvisionResult = {
  type: 'success' | 'error';
  message: string;
  orgSlug?: string;
};

const TRIAL_DURATION_OPTIONS = [
  { value: TrialDurationWeeks.TwoWeeks, label: '2 weeks' },
  { value: TrialDurationWeeks.FourWeeks, label: '4 weeks' },
  { value: TrialDurationWeeks.SixWeeks, label: '6 weeks' },
  { value: TrialDurationWeeks.EightWeeks, label: '8 weeks' },
];

export const ProvisioningSection = () => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [tier, setTier] = useState<OrganizationTier>(OrganizationTier.Free);
  const [trialDurationWeeks, setTrialDurationWeeks] = useState<TrialDurationWeeks>(TrialDurationWeeks.FourWeeks);
  const [result, setResult] = useState<ProvisionResult | null>(null);

  const [provisionOrg, { loading }] = useAdminProvisionOrganizationMutation({
    onCompleted: (data) => {
      const result = data.adminProvisionOrganization;
      if (!result) {
        setResult({
          type: 'error',
          message: 'No response from server',
        });
        return;
      }
      if ('code' in result) {
        // Error case
        setResult({
          type: 'error',
          message: result.message,
        });
      } else {
        // Success case
        setResult({
          type: 'success',
          message: `Organization "${result.name}" created successfully!`,
          orgSlug: result.slug,
        });
        // Clear form
        setName('');
        setSlug('');
        setTier(OrganizationTier.Free);
        setTrialDurationWeeks(TrialDurationWeeks.FourWeeks);
      }
    },
    onError: (error) => {
      setResult({
        type: 'error',
        message: error.message,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setResult(null);

    // Client-side validation
    if (name.trim().length < 1) {
      setResult({
        type: 'error',
        message: 'Organization name is required',
      });
      return;
    }

    if (slug.trim().length < 4) {
      setResult({
        type: 'error',
        message: 'Organization slug must be at least 4 characters',
      });
      return;
    }

    provisionOrg({
      variables: {
        name: name.trim(),
        slug: slug.trim(),
        tier,
        trialDurationWeeks: tier === OrganizationTier.ProTrial ? trialDurationWeeks : undefined,
      },
    });
  };

  // Auto-generate slug from name
  const handleNameChange = (value: string) => {
    // Check if current slug matches what was auto-generated from the old name
    const oldGeneratedSlug = generateSlug(name);
    const userHasManuallyEditedSlug = slug && slug !== oldGeneratedSlug;

    setName(value);

    // Only auto-generate slug if user hasn't manually edited it
    if (!userHasManuallyEditedSlug) {
      setSlug(generateSlug(value));
    }
  };

  const generateSlug = (value: string) => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold mb-4 text-gray-900">
        Provision New Organization
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="org-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Organization Name
          </label>
          <input
            id="org-name"
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Acme Corporation"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm"
          />
        </div>

        <div>
          <label
            htmlFor="org-slug"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Organization Slug
          </label>
          <input
            id="org-slug"
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="acme-corp"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 font-mono text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            URL-safe identifier, minimum 4 characters
          </p>
        </div>

        <div>
          <label
            htmlFor="org-tier"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Plan Tier
          </label>
          <select
            id="org-tier"
            value={tier}
            onChange={(e) => setTier(e.target.value as OrganizationTier)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm"
          >
            <option value={OrganizationTier.Free}>FREE</option>
            <option value={OrganizationTier.Pro}>PRO</option>
            <option value={OrganizationTier.ProTrial}>PRO_TRIAL</option>
            <option value={OrganizationTier.LegacyFree}>LEGACY_FREE</option>
          </select>
        </div>

        {tier === OrganizationTier.ProTrial && (
          <div>
            <label
              htmlFor="trial-duration"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Trial Duration
            </label>
            <select
              id="trial-duration"
              value={trialDurationWeeks}
              onChange={(e) => setTrialDurationWeeks(e.target.value as TrialDurationWeeks)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm"
            >
              {TRIAL_DURATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Trial will expire after the selected duration
            </p>
          </div>
        )}

        {result && (
          <div
            className={`p-4 rounded-md text-sm ${
              result.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            {result.message}
            {result.orgSlug && (
              <div className="mt-2 flex items-center gap-3">
                <span className="font-mono text-xs">
                  Slug: {result.orgSlug}
                </span>
                <Link
                  to={routes.org.getPath({ orgSlug: result.orgSlug })}
                  className="text-xs font-medium text-green-700 hover:text-green-900 underline"
                >
                  View organization →
                </Link>
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !name.trim() || slug.trim().length < 4}
          className="px-6 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
        >
          {loading ? 'Creating...' : 'Create Organization'}
        </button>
      </form>
    </section>
  );
};
